import { formatDate } from '@utils/dates/date.utils';
import { EMPTY_VALUE } from '../constants';
import { EnrolleeType, IAddress, IEnrolleeCount } from '../types';

export {
    HISTORICAL_YEAR_UPPER_BOUND,
    HISTORICAL_YEAR_LOWER_BOUND,
    getCoverageYearsWithHistorical,
    getHistoricalYears,
    getYearsDescending,
} from './yearRange.utils';

// Re-export types used by utilities
export type { IEnrolleeCount } from '../types';

// ============================================================================
// Validation Utilities
// ============================================================================

export const isValidAmount = (value: string | number | null | undefined): boolean => {
    if (value == null) return false;
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    return !isNaN(numValue) && numValue >= 0;
};

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Wraps formatDate with error handling so a malformed date string
 * does not crash the calling component.
 */
export const safeFormatDate = (
    date: string | undefined | null,
    format: string,
    fallback: string = EMPTY_VALUE,
): string => {
    if (!date) return fallback;
    try {
        return formatDate(date, format);
    } catch {
        return date;
    }
};

export const formatAddress = (address?: IAddress): string => {
    if (!address) return EMPTY_VALUE;
    const parts: string[] = [];
    
    if (address.addressLine1) {
        parts.push(address.addressLine1);
    }
    if (address.addressLine2) {
        parts.push(address.addressLine2);
    }
    if (address.city && address.state && address.zipCode != null) {
        parts.push(`${address.city}, ${address.state} ${address.zipCode}`);
    }

    const addressStr = parts.length > 0 ? parts.join(', ') : '';
    
    if (address.fipCode != null) {
        return addressStr ? `${addressStr} (${address.fipCode})` : `(${address.fipCode})`;
    }
    
    return addressStr || EMPTY_VALUE;
};

export const formatCityState = (city?: string | null, state?: string | null): string => {
    if (!city && !state) return EMPTY_VALUE;
    if (city && state) return `${city}, ${state}`;
    return city || state || EMPTY_VALUE;
};

// ============================================================================
// CSS Class Utilities
// ============================================================================

export const getRowClassName = (isTotal: boolean): string => {
    const baseClasses = 'display-flex flex-justify flex-align-center padding-y-1';
    return isTotal 
        ? `${baseClasses} border-top-1px border-base-lighter margin-top-1 padding-top-2 text-bold` 
        : baseClasses;
};

export const getEligibilityRowClassName = (index: number, totalCount: number): string => {
    // margin-right-2 prevents content from touching the right edge of containers
    const baseClasses = 'display-flex flex-justify flex-align-start padding-y-3 margin-right-2';
    const isLastRow = index === totalCount - 1;
    const showBorder = totalCount > 1 && !isLastRow;
    return showBorder ? `${baseClasses} border-bottom-1px border-base-lighter` : baseClasses;
};

// ============================================================================
// Enrollee Count Utilities
// ============================================================================

// Pre-computed prefixes from enum values (e.g., 'SELF' from 'Self(18)')
const SELF_PREFIX = EnrolleeType.SELF.split('(')[0].trim().toUpperCase();
const SPOUSE_PREFIX = EnrolleeType.SPOUSE.split('(')[0].trim().toUpperCase();

/**
 * Calculates the count of primary, spouse, and dependent enrollees.
 * @param enrollees - Array of enrollee objects
 * @param getRelationship - Function to extract the relationship/type from each enrollee
 * @returns Object with primary, spouse, and dependent counts
 */
export const getEnrolleeCount = <T>(
    enrollees: T[],
    getRelationship: (enrollee: T) => string | undefined | null
): IEnrolleeCount => {
    return enrollees.reduce(
        (counts, enrollee) => {
            const relationship = (getRelationship(enrollee) || '').trim().toUpperCase();
            if (relationship.startsWith(SELF_PREFIX)) {
                counts.primary++;
            } else if (relationship.startsWith(SPOUSE_PREFIX)) {
                counts.spouse++;
            } else if (relationship) {
                counts.dependent++;
            }
            return counts;
        },
        { primary: 0, spouse: 0, dependent: 0 }
    );
};
